import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Equal, Plus, ArrowLeft, AlertTriangle } from "lucide-react";
import TextInputComp from "../../../components/customer/TextInputComp";
import CommonButton from "../../../components/CommonButton";
import ProductSelectorDropdown from "../../../components/Admin/ProductSelectorDropdown";
import ProductCard from "../../../components/Admin/ProductCard";
import {
  useCreateBundleMutation,
  useUpdateBundleMutation,
  useLazyGetBundleByIdQuery,
  useLazyGetAdminProductsQuery,
} from "../../../redux/actions/Admin/ProductAdmin";
import { useAlert } from "../../../components/customer/AlertContext";

const AdminCreateBundlesScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editingBundleId = location.state?.bundleId || null;

  // ------------------------------------------------
  // Form States
  // ------------------------------------------------

  const [bundleName, setBundleName] = useState("");
  const [bundleDescription, setBundleDescription] = useState("");

  const [product1, setProduct1] = useState<any | null>(null);

  const [product2, setProduct2] = useState<any | null>(null);

  const [bundlePrice, setBundlePrice] = useState("");

  const [error, setError] = useState<string | null>(null);

  const [activeSelect, setActiveSelect] = useState<
    "product1" | "product2" | null
  >(null);
    const { showConfirm } = useAlert();

  // ------------------------------------------------
  // API Hooks
  // ------------------------------------------------

  const [triggerGetProducts, { data: productsCatalog }] =
    useLazyGetAdminProductsQuery();

  const [triggerGetBundleDetails] = useLazyGetBundleByIdQuery();

  const [createBundle, { isLoading: isCreating }] = useCreateBundleMutation();

  const [updateBundle, { isLoading: isUpdating }] = useUpdateBundleMutation();

  // ------------------------------------------------
  // Load products
  // ------------------------------------------------

  useEffect(() => {
    triggerGetProducts({
      pageSize: 100,
      pageNumber: 1,
    });
  }, [triggerGetProducts]);

  // ------------------------------------------------
  // Load bundle when editing
  // ------------------------------------------------

  useEffect(() => {
    if (!editingBundleId) {
      return;
    }

    triggerGetBundleDetails(editingBundleId)
      .unwrap()
      .then((res) => {
        if (!res?.data) {
          return;
        }

        setBundleName(res.data.name || "");

        setBundleDescription(res.data.description || "");

        setBundlePrice(res.data.price?.toString() || "");

        if (res.data.items?.[0]) {
          setProduct1(res.data.items[0]);
        }

        if (res.data.items?.[1]) {
          setProduct2(res.data.items[1]);
        }
      })
      .catch(() => {
        setError("Failed to load bundle details.");
      });
  }, [editingBundleId, triggerGetBundleDetails]);

  // ------------------------------------------------
  // Helpers
  // ------------------------------------------------

  const getProductId = (product: any): number | null => {
    if (!product) {
      return null;
    }

    return product.productId ?? product.id ?? null;
  };

  const getProductName = (product: any): string => {
    return product?.productName || product?.name || "Selected product";
  };

  const getNumericPrice = (price: any): number => {
    if (price === null || price === undefined || price === "") {
      return 0;
    }

    if (typeof price === "number") {
      return price;
    }

    const parsed = parseFloat(price.toString().replace(/[^\d.]/g, ""));

    return Number.isFinite(parsed) ? parsed : 0;
  };

  const totalPrice =
    getNumericPrice(product1?.price) + getNumericPrice(product2?.price);

  // ------------------------------------------------
  // Frontend Bundle Validation
  //
  // Backend remains the final authority.
  // ------------------------------------------------

  const validateBundle = (): string | null => {
    // --------------------------------------------
    // Bundle name
    // --------------------------------------------

    if (!bundleName.trim()) {
      return "Please enter a bundle name.";
    }

    // --------------------------------------------
    // Products
    // --------------------------------------------

    if (!product1 || !product2) {
      return "Please select both products.";
    }

    const product1Id = getProductId(product1);

    const product2Id = getProductId(product2);

    if (product1Id === null || product2Id === null) {
      return "Invalid product selection.";
    }

    // --------------------------------------------
    // Duplicate product
    // --------------------------------------------

    if (product1Id === product2Id) {
      return "The same product cannot be added to a bundle more than once.";
    }

    // --------------------------------------------
    // Product validation
    //
    // These fields already exist in the
    // common Admin Products API.
    // --------------------------------------------

    const selectedProducts = [product1, product2];

    for (const product of selectedProducts) {
      const productName = getProductName(product);

      // Inactive
      if (product.isActive === false) {
        return `Product "${productName}" is inactive and cannot be added to a bundle.`;
      }

      // Unpublished
      if (product.isPublished === false) {
        return `Product "${productName}" is not published and cannot be added to a bundle.`;
      }

      // Forced product
      if (product.isForceProduct === true) {
        return `Product "${productName}" is a forced product and cannot be added to a bundle.`;
      }

      /*
       * IMPORTANT:
       *
       * We intentionally DO NOT reject stock === 0.
       *
       * Inventory is checked when the customer
       * actually adds the bundle to cart.
       */
    }

    // --------------------------------------------
    // Bundle price
    // --------------------------------------------

    const customPrice =
      bundlePrice.trim() === "" ? totalPrice : Number(bundlePrice);

    if (!Number.isFinite(customPrice) || customPrice <= 0) {
      return "Bundle price must be greater than zero.";
    }

    // --------------------------------------------
    // Bundle should not cost more than
    // the standard product price.
    // --------------------------------------------

    if (customPrice > totalPrice) {
      return "Bundle price cannot be greater than the standard product price.";
    }

    return null;
  };

  // ------------------------------------------------
  // Save
  // ------------------------------------------------

  const handleFormSave = async () => {
    setError(null);

    // ----------------------------------------
    // Frontend validation
    // ----------------------------------------

    const validationError = validateBundle();

    if (validationError) {
      setError(validationError);

      return;
    }

    const product1Id = getProductId(product1);

    const product2Id = getProductId(product2);

    if (product1Id === null || product2Id === null) {
      setError("Invalid product selection.");

      return;
    }

    const customPrice =
      bundlePrice.trim() === "" ? totalPrice : Number(bundlePrice);

    // ----------------------------------------
    // Payload
    //
    // Quantity remains fixed at 1.
    // Therefore frontend can never send 0.
    // Backend still validates quantity > 0.
    // ----------------------------------------

    const payload = {
      name: bundleName.trim(),

      description: bundleDescription.trim(),

      imageUrl: product1.imageUrl || product1.thumbnail || "",

      price: customPrice,

      items: [
        {
          productId: product1Id,

          quantity: 1,
        },
        {
          productId: product2Id,

          quantity: 1,
        },
      ],
    };

    try {
      if (editingBundleId) {
        await updateBundle({
          id: editingBundleId,

          data: payload,
        }).unwrap();
      } else {
        await createBundle(payload).unwrap();
      }

      navigate(-1);
    } catch (err: any) {
      // ------------------------------------
      // Show backend validation message
      // ------------------------------------

      const backendMessage =
        err?.data?.Message || err?.data?.error || err?.message;

      setError(backendMessage || "Failed to save bundle changes.");
 
    }
  };

  // ------------------------------------------------
  // Product selection
  // ------------------------------------------------

  const handleProductSelect = (product: any) => {
    setError(null);

    const selectedProductId = getProductId(product);

    if (selectedProductId === null) {
      setError("Invalid product selection.");

      return;
    }

    // --------------------------------------------
    // Prevent selecting same product
    // --------------------------------------------

    if (
      activeSelect === "product1" &&
      getProductId(product2) === selectedProductId
    ) {
      setError("The same product cannot be added to a bundle more than once.");

      return;
    }

    if (
      activeSelect === "product2" &&
      getProductId(product1) === selectedProductId
    ) {
      setError("The same product cannot be added to a bundle more than once.");

      return;
    }

    // --------------------------------------------
    // Forced product
    // --------------------------------------------

    if (product.isForceProduct === true) {
      setError(
        `Product "${getProductName(product)}" is a forced product and cannot be added to a bundle.`,
      );

      return;
    }

    // --------------------------------------------
    // Inactive
    // --------------------------------------------

    if (product.isActive === false) {
      setError(
        `Product "${getProductName(product)}" is inactive and cannot be added to a bundle.`,
      );

      return;
    }

    // --------------------------------------------
    // Unpublished
    // --------------------------------------------

    if (product.isPublished === false) {
      setError(
        `Product "${getProductName(product)}" is not published and cannot be added to a bundle.`,
      );

      return;
    }

    // --------------------------------------------
    // Select
    // --------------------------------------------

    if (activeSelect === "product1") {
      setProduct1(product);
    } else if (activeSelect === "product2") {
      setProduct2(product);
    }

    setActiveSelect(null);
  };

  // ------------------------------------------------
  // Render
  // ------------------------------------------------

  return (
    <div className="p-5 text-left antialiased max-w-6xl mx-auto space-y-6">
      {/* BACK BUTTON */}

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 px-4 py-2 rounded-xl hover:bg-slate-50 transition shadow-2xs cursor-pointer"
      >
        <ArrowLeft size={14} className="text-[#2F5E2E] stroke-[3]" />

        <span>Back</span>
      </button>

      {/* HEADER */}

      <div className="mb-6 border-b border-slate-100 pb-4">
        <h1 className="text-2xl font-bold text-logoHeading">
          {editingBundleId ? "Edit Bundle" : "Create New Bundle"}
        </h1>

        <p className="text-sm text-gray-500 mt-1">
          Combine two products together into a single package with custom
          pricing.
        </p>
      </div>

      {/* INFO FIELDS */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-white p-5 rounded-2xl border border-slate-200">
        <div className="space-y-1">
          <label className="text-xs font-bold text-gray-600 uppercase">
            Bundle Name
          </label>

          <TextInputComp
            value={bundleName}
            onChangeText={(value) => {
              setBundleName(value);

              setError(null);
            }}
            placeholder="E.g., Special Combo Pack"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-bold text-gray-600 uppercase">
            Description
          </label>

          <TextInputComp
            value={bundleDescription}
            onChangeText={setBundleDescription}
            placeholder="E.g., Buy together and save more"
          />
        </div>
      </div>

      {/* SELECTION AREA */}

      <div className="flex flex-col lg:flex-row items-stretch gap-4">
        <ProductCard
          title="Select Product 1"
          product={product1}
          onClick={() => setActiveSelect("product1")}
          onRemove={() => setProduct1(null)}
        />

        <div className="flex items-center justify-center p-2">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 shrink-0 shadow-3xs">
            <Plus size={18} />
          </div>
        </div>

        <ProductCard
          title="Select Product 2"
          product={product2}
          onClick={() => setActiveSelect("product2")}
          onRemove={() => setProduct2(null)}
        />

        <div className="flex items-center justify-center p-2">
          <div className="w-10 h-10 rounded-full bg-logoHeading text-white flex items-center justify-center shrink-0 shadow-3xs">
            <Equal size={18} />
          </div>
        </div>

        {/* PRICING */}

        <div className="min-w-[260px] rounded-2xl border border-gray-200 bg-white p-5 shadow-3xs flex flex-col justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
              Total Standard Price
            </p>

            <h1 className="text-2xl font-black text-mainBrandColor mt-1">
              AED {totalPrice.toFixed(2)}
            </h1>
          </div>

          {/* OUT OF STOCK WARNING */}

          {(product1?.stock <= 0 || product2?.stock <= 0) && (
            <div className="flex items-start gap-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />

              <span>
                One or more selected products are currently out of stock. The
                bundle can still be created, but customers cannot purchase it
                until stock is available.
              </span>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
              Custom Bundle Price (AED)
            </label>

            <TextInputComp
              value={bundlePrice}
              onChangeText={(value) => {
                setBundlePrice(value);

                setError(null);
              }}
              placeholder={`E.g., ${(totalPrice * 0.85).toFixed(2)}`}
            />
          </div>

          {/* ERROR */}

          {error && (
            <div className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 p-2 rounded-lg">
              {error}
            </div>
          )}

          <CommonButton
            onClick={handleFormSave}
            disabled={isCreating || isUpdating}
            text={
              isCreating || isUpdating
                ? "Saving..."
                : editingBundleId
                  ? "Update Bundle"
                  : "Create Bundle"
            }
            variant="filled"
            rounded="md"
            className="w-full h-10 bg-[#2F5E2E] hover:bg-[#1f3f1e]"
          />
        </div>
      </div>

      {/* PRODUCT SELECTOR */}

      <ProductSelectorDropdown
        open={!!activeSelect}
        products={productsCatalog?.products || []}
        placeholder={
          activeSelect === "product1" ? "Search Product 1" : "Search Product 2"
        }
        onSelect={handleProductSelect}
        onClose={() => setActiveSelect(null)}
      />
    </div>
  );
};

export default AdminCreateBundlesScreen;
